/**
 * HandoffAudioPanel — the browser media-plane surface for a staff handoff.
 *
 * The ONLY place in apps/platform that touches the LiveKit media plane, and it
 * does so exclusively through `@hostesa/livekit-client` — never the raw LiveKit
 * SDK (master prompt §6/§13). Flow:
 *   permitted handoff -> useHandoffRoomToken(callId) -> opaque backend token
 *                     -> useHandoffRoom(token)       -> HandoffAudioControls
 *
 * GATING (all three; UI hiding is UX only — the backend token endpoint is
 * authoritative): 1) `livekit_handoff` flag ON, 2) membership holds
 * `handoff:handle`, 3) the call reached a handoff state.
 *
 * Flag off -> renders NOTHING. Flag on but permission absent -> calm disabled
 * affordance. Every failure cause (token_denied, mic_denied, disconnected,
 * failed, and the documented backend-gap "unavailable") is surfaced with
 * TEXT + ICON (never color alone) and a data-testid. The spinner respects
 * prefers-reduced-motion.
 */

import { useAuth } from '@hostesa/auth-client';
import { useFlag } from '@hostesa/feature-flags';
import { useT } from '@hostesa/i18n';
import {
  HandoffAudioControls,
  type HandoffRoomState,
  useHandoffRoom,
} from '@hostesa/livekit-client';
import { cn } from '@hostesa/ui';
import { AlertTriangle, Headphones, Loader2, MicOff, PhoneOff, ShieldAlert } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';
import {
  type HandoffTokenFetcher,
  type HandoffTokenResult,
  useHandoffRoomToken,
} from '../../../lib/handoff-token';

type StatusTone = 'info' | 'warning' | 'error' | 'success';

interface StatusView {
  readonly testId: string;
  readonly icon: ReactNode;
  readonly titleKey: string;
  readonly bodyKey: string;
  readonly tone: StatusTone;
}

const TONE_CLASS: Record<StatusTone, string> = {
  info: 'text-[var(--color-stone-mid)]',
  warning: 'text-[var(--color-charcoal)]',
  error: 'text-[var(--color-alert)]',
  success: 'text-[var(--color-success)]',
};

/** Map a token-error reason to its presentation. */
function tokenErrorView(result: HandoffTokenResult & { status: 'error' }): StatusView {
  switch (result.reason) {
    case 'token_denied':
      return {
        testId: 'handoff-audio-token-denied',
        icon: <ShieldAlert aria-hidden />,
        titleKey: 'live.audio.states.tokenDenied.title',
        bodyKey: 'live.audio.states.tokenDenied.body',
        tone: 'error',
      };
    case 'unavailable':
      return {
        testId: 'handoff-audio-unavailable',
        icon: <AlertTriangle aria-hidden />,
        titleKey: 'live.audio.states.unavailable.title',
        bodyKey: 'live.audio.states.unavailable.body',
        tone: 'warning',
      };
    default:
      return {
        testId: 'handoff-audio-failed',
        icon: <AlertTriangle aria-hidden />,
        titleKey: 'live.audio.states.failed.title',
        bodyKey: 'live.audio.states.failed.body',
        tone: 'error',
      };
  }
}

/** Map a room state to its presentation (only the non-controls states). */
function roomStateView(state: HandoffRoomState): StatusView | null {
  switch (state) {
    case 'mic_denied':
      return {
        testId: 'handoff-audio-mic-denied',
        icon: <MicOff aria-hidden />,
        titleKey: 'live.audio.states.micDenied.title',
        bodyKey: 'live.audio.states.micDenied.body',
        tone: 'error',
      };
    case 'token_denied':
      return {
        testId: 'handoff-audio-token-denied',
        icon: <ShieldAlert aria-hidden />,
        titleKey: 'live.audio.states.tokenDenied.title',
        bodyKey: 'live.audio.states.tokenDenied.body',
        tone: 'error',
      };
    case 'disconnected':
      return {
        testId: 'handoff-audio-disconnected',
        icon: <PhoneOff aria-hidden />,
        titleKey: 'live.audio.states.disconnected.title',
        bodyKey: 'live.audio.states.disconnected.body',
        tone: 'warning',
      };
    case 'failed':
      return {
        testId: 'handoff-audio-failed',
        icon: <AlertTriangle aria-hidden />,
        titleKey: 'live.audio.states.failed.title',
        bodyKey: 'live.audio.states.failed.body',
        tone: 'error',
      };
    default:
      return null;
  }
}

export interface HandoffAudioPanelProps {
  /** Canonical call UUID currently in a handoff state. */
  callId: string;
  /**
   * Whether the call has actually reached a handoff state
   * (`handoff_requested` / `human_active`). The token is only requested once a
   * handoff is genuinely permitted.
   */
  handoffActive: boolean;
  /** Test seam: room factory from `@hostesa/livekit-client/mock`. */
  createRoom?: Parameters<typeof useHandoffRoom>[1];
  /** Test seam: token fetcher override (still only ever an opaque token). */
  fetchToken?: HandoffTokenFetcher;
}

export function HandoffAudioPanel({
  callId,
  handoffActive,
  createRoom,
  fetchToken,
}: HandoffAudioPanelProps): ReactElement | null {
  const { t } = useT('calls');
  const flagOn = useFlag('livekit_handoff');
  const { can } = useAuth();
  const canHandle = can('handoff:handle');

  // GATE 1: flag off -> render nothing at all.
  if (!flagOn) return null;

  // GATE 2: permission absent -> calm disabled affordance (no token request).
  if (!canHandle) {
    return (
      <HandoffStatus
        wrapTestId="handoff-audio-panel"
        view={{
          testId: 'handoff-audio-forbidden',
          icon: <ShieldAlert aria-hidden />,
          titleKey: 'live.audio.states.forbidden.title',
          bodyKey: 'live.audio.states.forbidden.body',
          tone: 'warning',
        }}
        t={t}
      />
    );
  }

  return (
    <HandoffAudioBridge
      callId={callId}
      handoffActive={handoffActive}
      createRoom={createRoom}
      fetchToken={fetchToken}
    />
  );
}

/**
 * Inner component mounted ONLY after the flag + permission gates pass, so the
 * token/room hooks never run for users who cannot handle a handoff.
 */
function HandoffAudioBridge({
  callId,
  handoffActive,
  createRoom,
  fetchToken,
}: {
  callId: string;
  handoffActive: boolean;
  createRoom?: Parameters<typeof useHandoffRoom>[1];
  fetchToken?: HandoffTokenFetcher;
}): ReactElement {
  const { t } = useT('calls');

  // GATE 3: only request a token once a handoff is genuinely permitted.
  const tokenResult = useHandoffRoomToken(handoffActive ? callId : null, fetchToken);
  const token = tokenResult.status === 'ready' ? tokenResult.token : null;

  const { state, muted, mute, unmute, leave } = useHandoffRoom(token, createRoom);

  return (
    <section
      data-testid="handoff-audio-panel"
      aria-label={t('live.audio.title')}
      className="flex flex-col gap-3 rounded-md border border-[var(--color-warm-stone)] bg-[var(--color-canvas)] p-4"
    >
      <header className="flex items-center gap-2">
        <Headphones aria-hidden className="size-4 text-[var(--color-stone-mid)]" />
        <h3 className="font-[var(--font-display)] text-sm font-medium text-[var(--color-charcoal)]">
          {t('live.audio.title')}
        </h3>
      </header>

      {!handoffActive ? (
        <Body
          testId="handoff-audio-pending"
          icon={<Headphones aria-hidden />}
          tone="info"
          title={t('live.audio.states.pending.title')}
          body={t('live.audio.states.pending.body')}
        />
      ) : tokenResult.status === 'loading' ? (
        <Body
          testId="handoff-audio-loading"
          icon={
            <Loader2
              aria-hidden
              className="size-4 motion-safe:animate-spin motion-reduce:animate-none"
            />
          }
          tone="info"
          title={t('live.audio.states.requesting.title')}
          body={t('live.audio.states.requesting.body')}
        />
      ) : tokenResult.status === 'error' ? (
        <ViewBody view={tokenErrorView(tokenResult)} t={t} />
      ) : (
        <RoomBody state={state} muted={muted} mute={mute} unmute={unmute} leave={leave} t={t} />
      )}
    </section>
  );
}

/** Renders the live room: connecting/connected controls + error states. */
function RoomBody({
  state,
  muted,
  mute,
  unmute,
  leave,
  t,
}: {
  state: HandoffRoomState;
  muted: boolean;
  mute: () => void;
  unmute: () => void;
  leave: () => void;
  t: ReturnType<typeof useT>['t'];
}): ReactElement {
  const errorView = roomStateView(state);
  if (errorView) {
    return <ViewBody view={errorView} t={t} />;
  }

  return (
    <div className="flex flex-col gap-2">
      {/*
       * Only `connected` shows the connected label (and enables the controls).
       * The pre-connect window (`idle` → `connecting`) shows the spinner — never
       * the connected label, so the controls' disabled state always matches the
       * shown status.
       */}
      {state === 'connected' ? (
        <p
          data-testid="handoff-audio-connected"
          className={cn('flex items-center gap-2 text-sm', TONE_CLASS.success)}
        >
          <Headphones aria-hidden className="size-4" />
          {t('live.audio.states.connected.title')}
        </p>
      ) : (
        <Body
          testId="handoff-audio-connecting"
          icon={
            <Loader2
              aria-hidden
              className="size-4 motion-safe:animate-spin motion-reduce:animate-none"
            />
          }
          tone="info"
          title={t('live.audio.states.connecting.title')}
          body={t('live.audio.states.connecting.body')}
        />
      )}

      <HandoffAudioControls
        state={state}
        muted={muted}
        onMute={mute}
        onUnmute={unmute}
        onLeave={leave}
      />
    </div>
  );
}

function ViewBody({
  view,
  t,
}: {
  view: StatusView;
  t: ReturnType<typeof useT>['t'];
}): ReactElement {
  return (
    <Body
      testId={view.testId}
      icon={view.icon}
      tone={view.tone}
      title={t(view.titleKey)}
      body={t(view.bodyKey)}
    />
  );
}

/** Top-level status wrapper used by the permission gate (no room hooks). */
function HandoffStatus({
  wrapTestId,
  view,
  t,
}: {
  wrapTestId: string;
  view: StatusView;
  t: ReturnType<typeof useT>['t'];
}): ReactElement {
  return (
    <section
      data-testid={wrapTestId}
      aria-label={t('live.audio.title')}
      className="flex flex-col gap-3 rounded-md border border-[var(--color-warm-stone)] bg-[var(--color-canvas)] p-4"
    >
      <header className="flex items-center gap-2">
        <Headphones aria-hidden className="size-4 text-[var(--color-stone-mid)]" />
        <h3 className="font-[var(--font-display)] text-sm font-medium text-[var(--color-charcoal)]">
          {t('live.audio.title')}
        </h3>
      </header>
      <ViewBody view={view} t={t} />
    </section>
  );
}

function Body({
  testId,
  icon,
  tone,
  title,
  body,
}: {
  testId: string;
  icon: ReactNode;
  tone: StatusTone;
  title: string;
  body: string;
}): ReactElement {
  return (
    <div data-testid={testId} role="status" aria-live="polite" className="flex flex-col gap-1">
      <p className={cn('flex items-center gap-2 text-sm font-medium', TONE_CLASS[tone])}>
        <span className="size-4 [&>svg]:size-4">{icon}</span>
        {title}
      </p>
      <p className="text-xs text-[var(--color-stone-mid)]">{body}</p>
    </div>
  );
}
